import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import useNotyf from "/@src/composable/useNotyf";
import { useI18n } from "vue-i18n";
import { watch, ref, computed } from "vue";
import { toISOLocal } from "/@src/utils/date";
import { excludeTypename } from '/@src/utils';
import { ContentKind } from "/@src/models/api";
import { TodosCalendar, WeekListOption, eventContents } from '/@src/models/calendarEvent';
import { compareDate, getWeekDate, getTodayStartDay, getTodayEndDay } from "/@src/utils/calendarEventDate";
import { ContentStatus, useCalendarEventUpdateMutation, useCalendarEventCreateMutation, CalendarEvent, Content, useContentListLazyQuery, useContentDetailQuery } from '/@src/__generated__/generated-gql-types'
import { useRoute, useRouter } from 'vue-router';

interface Options {
  startTime?:string
  endTime?:string
}
export default function useFinanceContent(options?: Options) {
  const { t } = useI18n()
  const hoverDate = ref<Date>(new Date())
  const currentDate = ref<Date>(new Date())
  const selectedDay = ref<Date>(new Date())
  const route = useRoute()
  const router = useRouter()
  const selectedMonth = ref<Date>(new Date())
  const selectedCurrentDay = ref<Date>(new Date())
  dayjs.extend(isoWeek)
  // 获取当前日期
  const notify = useNotyf()
  const isLoading = ref(false)
  const { load, result, refetch, loading } = useContentListLazyQuery({
    first: 9999,
    kinds: [ContentKind.FINANCE_EVENT],
    startTime: route.query.start?getTodayStartDay(new Date(parseInt(route.query.start as string))):options?.startTime,
    endTime: route.query.end?getTodayEndDay(new Date(parseInt(route.query.end as string))):options?.endTime,
    associateWithKinds:[ContentKind.CALENDAR_TOPIC],
    lite: false
  })

  //获取数据
  load()
  
  if (route.query.start) {
    selectedCurrentDay.value = new Date(parseInt(route.query.start as string))
    selectedDay.value = new Date(parseInt(route.query.start as string))
  }

  /**
   * 监听当前日期变化
   */
  watch(selectedDay, val => {
    if (val && route.path === '/content/events') {
      selectedCurrentDay.value = val
      router.push({
        query: {
          start: isNaN(Number(val)) ? new Date(getTodayStartDay(new Date())).getTime() : new Date(getTodayStartDay(val)).getTime(),
          end: isNaN(Number(val)) ? new Date(getTodayEndDay(new Date())).getTime() : new Date(getTodayEndDay(val)).getTime(),
        }
      })
    }
  })

  watch(route, () => {
    refetch({
      startTime: getTodayStartDay(new Date(parseInt(route.query.start as string))),
      endTime: getTodayEndDay(new Date(parseInt(route.query.end as string)))
    }) 
    selectedCurrentDay.value = new Date(parseInt(route.query.start as string))
    selectedDay.value = new Date(parseInt(route.query.start as string))
  })

  /**
   * 鼠标hover时间变化
   */
  watch(hoverDate, val => {
    currentDate.value = val
  })

  const calendarTodos = computed(() => {
    let arr: Array<TodosCalendar> = []
    if (result.value && result.value.contents && result.value.contents.items!.length > 0) {
      const content = result.value.contents.items!

      // 获取有事件的日期列表
      let dates: Array<Date> = content.map((item) => new Date(dayjs(item?.datetime).format('YYYY,MM,DD')))
      arr.push({
        description: null,
        isComplete: false,
        dates: dates,
      })

      // 筛选出所选日期的事件
      for (let i = 0; i < content.length; i++) {
        let itemDates: Array<Date> = [new Date(dayjs(content[i]?.datetime).format('YYYY,MM,DD'))]
        arr.push({
          description: content[i]?.title || null,
          isComplete: false,
          dates: itemDates,
        })
      }

      arr = arr.filter(x => x.description)
    }
    return arr
  })

  /**
   * 金融大事件 右侧内容
   */
  const selectedDayContents = computed<Array<Content>>(() => {
    if (result.value) {
      const content = result.value.contents?.items?.filter(item => compareDate(item?.datetime, selectedCurrentDay.value.toString()))
      return content as Array<Content>
    }
    return []
  })

  /**
   * 金融大事件 网页预览 list
   */
  const selectedWeekContents = computed<Array<WeekListOption>>(() => {
    if (result.value) {
      const content = result.value?.contents?.items!
      // 获取当前所选星期
      const weekOfDay = selectedCurrentDay.value.getDay() == 0 ? 7 : selectedCurrentDay.value.getDay()
      // 获取周一日期
      const startTime = dayjs(selectedCurrentDay.value).subtract(weekOfDay - 1, "day").toDate()
      // 获取周五日期
      const lastFriday = dayjs(selectedCurrentDay.value).subtract(weekOfDay - 5, "day").toDate()
      const endTime = new Date(new Date(lastFriday.toString()).toLocaleDateString()).getTime() + 24 * 60 * 60 * 1000 - 1
      // 获取当前所选范围的所有日期
      const weekDay = getWeekDate(startTime, new Date(endTime))
      const newData = weekDay.map((x) => {
        const item = content.filter((y) => dayjs(y?.datetime).format('YYYY-MM-DD') === x)
        return {
          date: x,
          content: item[0] || null
        }
      })
      return newData as Array<WeekListOption>
    }
    return []
  })
  
  const { mutate: createCalendarEvent } = useCalendarEventCreateMutation()
  const { mutate: updateCalendarEvent } = useCalendarEventUpdateMutation()

  const postFinanceForm = (form: eventContents, calendarTopics: Content[]) => {
    form.tag_relations = (form.tag_relations?.length??0) > 0 ? excludeTypename(form.tag_relations): undefined
    isLoading.value = true
    let isCreate = true
    if (form.id) {
      isCreate = false
    }
    if (isCreate) {
      if (!form.display_time) {
        form.display_time = toISOLocal(selectedCurrentDay.value)
      }
      form.datetime = form.display_time
      const content = [...result.value?.contents?.items!]
      content.unshift(form as Content)
      result.value = { 
        contents: {
          items: content.map((item) => ({
            ...item,
            tag_relations: item?.tag_relations,
            detail: {
              country: form.country,
              source: form.source,
              actual: form.actual,
              previous: form.previous,
              revised: form.revised,
              forecast: form.forecast,
              event: form.title,
              ...item?.detail
            }
          }))
        }
      }
      form.status = ContentStatus.Published
      form.event = form.title
      form.title = ''
      delete form.datetime
      updateCalendarTopic(form.id as number, calendarTopics)
      createCalendarEvent({
        event: form
      }).then(res => {
        form.id = res?.data?.calendarEventCreate.id!
        notify.success(t('event-save-complete'))
        const index = result.value?.contents?.items?.findIndex((item) => (
          item?.title == form.event
        ))
      
        if (index !== -1) { 
          const updatedItem = {
            ...result.value!.contents!.items![index!],
            raw_id: form.id
          }
    
          result.value!.contents!.items![index!] = updatedItem
        }
        updateCalendarTopic(form.id as number, calendarTopics)
      }).finally(() => {
        isLoading.value = false
    })
    } else {
      const newContent = JSON.parse(JSON.stringify(result.value?.contents?.items))
      for (const it of newContent) {
        // 去列表中更新title
        if (it!.raw_id === form.id) {
          it.title = form.country?form.country+form.title:form.title
          it.event = form.title
          it.detail = form
          it.datetime = form.display_time?.toString()
          result.value = {
            contents: {
              items: newContent.map((item: Content) => ({
                ...item,
                detail: {
                  ...item?.detail
                }
              }))
            }
          }
          delete form.datetime
          form.event = it.event
          form.title = ''
          updateCalendarEvent({
            event: form
          }).then(res => {
            notify.success(t('event-save-complete'))
            if (res?.data?.calendarEventUpdate.id === form.id) {
              updateCalendarTopic(form.id as number, calendarTopics)
            }
          }).finally(() => {
            isLoading.value = false
          })
          break
        }
      }
    }
  }

  const removeContent = async (form: CalendarEvent) => {
    form.tag_relations = (form.tag_relations?.length??0) > 0 ? excludeTypename(form.tag_relations) : []
    const content = [...result.value?.contents?.items!]
    content.map((item, index) => {
      if (item?.raw_id === form.id) {
        content.splice(index, 1)
      }
    })
    result.value = {
      contents: {
        items: content.map((item) => ({
          ...item
        }))
      }
    }
    isLoading.value = true
    form.status = ContentStatus.Deleted
    updateCalendarEvent({
      event: form
    }).then(res => {
      notify.success(t('event-delete-success'))
    }).catch((err) => {
      console.log(err)
    }).finally(() => {
      isLoading.value = false
    })
  }

  const updateCalendarTopic = (id: number, calendarTopics: Content[]) => {
    const newContent = JSON.parse(JSON.stringify(result.value?.contents?.items))
    result.value = {
      contents: {
        items: newContent.map((item: Content) => {
          if (item.raw_id === id) { 
            return {
              ...item,
              associate_with: {
                items: calendarTopics,
              },
              detail: {
                ...item.detail,
              } 
            }
          } else {
            return item
          }
        })
      }
    }
  }

  return {
    loading,
    isLoading,
    hoverDate,
    selectedDay,
    selectedMonth,
    selectedDayContents,
    selectedWeekContents,
    calendarTodos,
    postFinanceForm,
    removeContent,
    load
  }
}
